import React, { useState, useEffect } from 'react';
import { interviewReadinessAPI } from '../services/api';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import MessageDisplay from '../components/MessageDisplay';
import FilterBar from '../components/FilterBar/FilterBar';
import DataCard from '../components/DataCard/DataCard';
import { formatDate } from '../utils/formatUtils';
import useAsyncOperation from '../hooks/useAsyncOperation';
import useInterviewReadinessData from '../hooks/useInterviewReadinessData';
import { matchesInterviewReadinessFilters } from './interviewReadinessFilters';
import {
  DEFAULT_INTERVIEW_READINESS_FORM_DATA,
  mapReadinessToFormData,
  boolToSelectValue,
  selectValueToBool,
  prepareInterviewReadinessPayload,
} from './interviewReadinessFormData';
import InterviewReadinessStudentViewModal from './InterviewReadinessStudentViewModal';
import { formatBatchDisplayLabel } from '../domain/batch/batchDisplayLabel';
import StaffPageShell from '../components/StaffPageShell';

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const InterviewReadinessPage = () => {
  const [filter, setFilter] = useState('all'); // all, ready, not-ready, no-data
  const { loading, error, success, execute, setSuccess } = useAsyncOperation();
  const { enrollments, batches, readinessMap, fetchData, getReadinessStatus } = useInterviewReadinessData({ execute });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [viewEnrollment, setViewEnrollment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [formData, setFormData] = useState({ ...DEFAULT_INTERVIEW_READINESS_FORM_DATA });

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = async (enrollment) => {
    setSelectedEnrollment(enrollment);
    setShowModal(true);

    const cached = readinessMap[enrollment.id];
    try {
      const response = await interviewReadinessAPI.getByEnrollment(enrollment.id);
      if (response.data) {
        setFormData(mapReadinessToFormData(response.data));
        return;
      }
    } catch (err) {
      if (cached) {
        setFormData(mapReadinessToFormData(cached));
        return;
      }
      setFormData({ ...DEFAULT_INTERVIEW_READINESS_FORM_DATA });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEnrollment) return;

    const payload = prepareInterviewReadinessPayload(formData);
    const response = await execute(
      async () => await interviewReadinessAPI.upsert(selectedEnrollment.id, payload),
      { successMessage: 'Interview readiness data saved successfully!' }
    );
    
    if (response) {
      setShowModal(false);
      setSelectedEnrollment(null);
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleViewStudent = (enrollment) => {
    setViewEnrollment(enrollment);
    setShowViewModal(true);
  };

  const filteredEnrollments = enrollments.filter((enrollment) =>
    matchesInterviewReadinessFilters({
      enrollment,
      filter,
      selectedBatch,
      searchTerm,
      getReadinessStatus
    })
  );


  const renderRatingStars = (rating) => {
    if (rating === null || rating === undefined) return 'N/A';
    return '⭐'.repeat(rating) + '○'.repeat(5 - rating);
  };

  return (
    <StaffPageShell title="Interview Readiness" wide>
        <MessageDisplay error={error} success={success && !showModal ? success : ''} />

        {/* Filters */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by name, email, phone, domain, batch, or student ID..."
          filters={[
            {
              name: 'filter',
              label: 'Filter by Status',
              value: filter,
              onChange: setFilter,
              type: 'select',
              options: [
                { value: 'all', label: 'All Enrollments' },
                { value: 'ready', label: 'Interview Ready' },
                { value: 'not-ready', label: 'Not Ready' },
                { value: 'no-data', label: 'No Data' }
              ]
            },
            {
              name: 'selectedBatch',
              label: 'Filter by Batch',
              value: selectedBatch,
              onChange: setSelectedBatch,
              type: 'select',
              options: [
                { value: '', label: 'All Batches' },
                ...batches.map(batch => ({
                  value: batch.id,
                  label: formatBatchDisplayLabel(batch),
                }))
              ]
            }
          ]}
        />

        {loading && !enrollments.length ? (
          <LoadingSpinner message="Loading interview readiness data..." />
        ) : filteredEnrollments.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary">No enrollments found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredEnrollments.map((enrollment) => {
              const readiness = readinessMap[enrollment.id];
              const status = getReadinessStatus(enrollment.id);
              
              return (
                <DataCard
                  key={enrollment.id}
                  title={enrollment.student?.fullName || 'Unknown Student'}
                  badges={[
                    { label: status.label, className: status.color }
                  ]}
                  metadata={[
                    { label: 'Batch', value: enrollment.batch?.batchName || 'N/A' },
                    { label: 'Course', value: enrollment.batch?.course?.courseName || 'N/A' },
                    { label: 'Email', value: enrollment.student?.emailAddress || 'N/A' },
                    { label: 'Phone', value: enrollment.student?.phoneNumber || 'N/A' },
                    { label: 'Domain', value: enrollment.student?.domain?.domainName || 'N/A' },
                    { label: 'Student ID', value: enrollment.student?.id || 'N/A' },
                    { label: 'Enrolled', value: formatDate(enrollment.enrollmentDate) }
                  ]}
                  actions={[
                    {
                      label: 'View Student',
                      onClick: () => handleViewStudent(enrollment),
                      variant: 'view'
                    },
                    { 
                      label: readiness ? 'Edit' : 'Add Readiness', 
                      onClick: () => handleEdit(enrollment), 
                      variant: 'edit',
                      icon: <span className="text-sm">{readiness ? '✏️' : '➕'}</span>
                    }
                  ]}
                >
                  {readiness && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium theme-text-secondary">Modules Complete:</span>
                          <span className="ml-2 theme-text">{readiness.allModulesComplete ? '✅' : '❌'}</span>
                        </div>
                        <div>
                          <span className="font-medium theme-text-secondary">Project Submitted:</span>
                          <span className="ml-2 theme-text">{readiness.projectSubmitted ? '✅' : '❌'}</span>
                        </div>
                        <div>
                          <span className="font-medium theme-text-secondary">Resume Ready:</span>
                          <span className="ml-2 theme-text">{readiness.resumeReadySubmitted ? '✅' : '❌'}</span>
                        </div>
                        <div>
                          <span className="font-medium theme-text-secondary">Mock Interviews:</span>
                          <span className="ml-2 theme-text">{readiness.numberOfMockInterviews || 0}</span>
                        </div>
                        {readiness.communicationSkillsRating && (
                          <div>
                            <span className="font-medium theme-text-secondary">Communication:</span>
                            <span className="ml-2 theme-text">{renderRatingStars(readiness.communicationSkillsRating)}</span>
                          </div>
                        )}
                        {readiness.technicalReadinessRating && (
                          <div>
                            <span className="font-medium theme-text-secondary">Technical:</span>
                            <span className="ml-2 theme-text">{renderRatingStars(readiness.technicalReadinessRating)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </DataCard>
              );
            })}
          </div>
        )}

        <InterviewReadinessStudentViewModal
          isOpen={showViewModal}
          enrollment={viewEnrollment}
          readiness={viewEnrollment ? readinessMap[viewEnrollment.id] : null}
          getReadinessStatus={getReadinessStatus}
          onClose={() => {
            setShowViewModal(false);
            setViewEnrollment(null);
          }}
        />

        <Modal
          isOpen={showModal && !!selectedEnrollment}
          onClose={() => {
            setShowModal(false);
            setSelectedEnrollment(null);
          }}
          title={`Interview Readiness - ${selectedEnrollment?.student?.fullName || 'Student'}`}
          size="xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
                {error && showModal && (
                  <div className="p-3 rounded-lg text-sm" style={{
                    backgroundColor: 'var(--color-error-50)',
                    border: '1px solid var(--color-error-200)',
                    color: 'var(--color-error-700)',
                  }}>
                    {error}
                  </div>
                )}
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.inPlacementGroup}
                      onChange={(e) => setFormData({ ...formData, inPlacementGroup: e.target.checked })}
                      className="mr-2"
                    />
                    In Placement Group
                  </label>
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.inMockGroup}
                      onChange={(e) => setFormData({ ...formData, inMockGroup: e.target.checked })}
                      className="mr-2"
                    />
                    In Mock Group
                  </label>
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.inUnderpreparedGroup}
                      onChange={(e) =>
                        setFormData({ ...formData, inUnderpreparedGroup: e.target.checked })
                      }
                      className="mr-2"
                    />
                    In Underprepared Group
                  </label>
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.allModulesComplete}
                      onChange={(e) => setFormData({ ...formData, allModulesComplete: e.target.checked })}
                      className="mr-2"
                    />
                    All Modules Complete
                  </label>
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.projectSubmitted}
                      onChange={(e) => setFormData({ ...formData, projectSubmitted: e.target.checked })}
                      className="mr-2"
                    />
                    Project Submitted
                  </label>
                  <label className="flex items-center theme-text">
                    <input
                      type="checkbox"
                      checked={formData.resumeReadySubmitted}
                      onChange={(e) => setFormData({ ...formData, resumeReadySubmitted: e.target.checked })}
                      className="mr-2"
                    />
                    Resume Ready & Submitted
                  </label>
                </div>

                {/* Readiness Assessment */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold theme-text mb-3">Readiness Assessment</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block theme-text mb-1">Student Readiness</label>
                      <SearchableSelect
                        value={boolToSelectValue(formData.studentReadiness)}
                        onChange={(e) => setFormData({ ...formData, studentReadiness: selectValueToBool(e.target.value) })}
                        className="w-full"
                        placeholder="Not Set"
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Trainer Readiness</label>
                      <SearchableSelect
                        value={boolToSelectValue(formData.trainerReadiness)}
                        onChange={(e) => setFormData({ ...formData, trainerReadiness: selectValueToBool(e.target.value) })}
                        className="w-full"
                        placeholder="Not Set"
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                  </div>
                </div>

                {/* Resume Information */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold theme-text mb-3">Resume Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block theme-text mb-1">Resume Submission Deadline</label>
                      <input
                        type="date"
                        value={formData.resumeSubmissionDeadline}
                        onChange={(e) => setFormData({ ...formData, resumeSubmissionDeadline: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Resume Reviewed</label>
                      <SearchableSelect
                        value={boolToSelectValue(formData.resumeReviewed)}
                        onChange={(e) => setFormData({ ...formData, resumeReviewed: selectValueToBool(e.target.value) })}
                        className="w-full"
                        placeholder="Not Set"
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Resume Changes Needed</label>
                      <SearchableSelect
                        value={boolToSelectValue(formData.resumeChangesNeeded)}
                        onChange={(e) => setFormData({ ...formData, resumeChangesNeeded: selectValueToBool(e.target.value) })}
                        className="w-full"
                        placeholder="Not Set"
                        options={YES_NO_OPTIONS}
                      />
                    </div>
                  </div>
                </div>

                {/* Mock Interview Information */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold theme-text mb-3">Mock Interview</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="flex items-center theme-text">
                      <input
                        type="checkbox"
                        checked={formData.mockInterviewDone}
                        onChange={(e) => setFormData({ ...formData, mockInterviewDone: e.target.checked })}
                        className="mr-2"
                      />
                      Mock Interview Done
                    </label>
                    <div>
                      <label className="block theme-text mb-1">Number of Mock Interviews</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.numberOfMockInterviews}
                        onChange={(e) => setFormData({ ...formData, numberOfMockInterviews: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Ratings */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold theme-text mb-3">Ratings (1-5)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block theme-text mb-1">Communication Skills</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={formData.communicationSkillsRating || ''}
                        onChange={(e) => setFormData({ ...formData, communicationSkillsRating: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Technical Readiness</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={formData.technicalReadinessRating || ''}
                        onChange={(e) => setFormData({ ...formData, technicalReadinessRating: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Attitude</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={formData.attitudeRating || ''}
                        onChange={(e) => setFormData({ ...formData, attitudeRating: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Presentation</label>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={formData.presentationRating || ''}
                        onChange={(e) => setFormData({ ...formData, presentationRating: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold theme-text mb-3">Additional Information</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block theme-text mb-1">Areas to Improve</label>
                      <textarea
                        value={formData.areasToImprove}
                        onChange={(e) => setFormData({ ...formData, areasToImprove: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                        rows="3"
                        placeholder="List areas where the student needs improvement..."
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Feedback Score</label>
                      <input
                        type="text"
                        value={formData.feedbackScore}
                        onChange={(e) => setFormData({ ...formData, feedbackScore: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                        placeholder="e.g., A+, Excellent, Good, etc."
                      />
                    </div>
                    <div>
                      <label className="block theme-text mb-1">Interview Ready</label>
                      <SearchableSelect
                        value={boolToSelectValue(formData.interviewReady)}
                        onChange={(e) => setFormData({ ...formData, interviewReady: selectValueToBool(e.target.value) })}
                        className="w-full"
                        placeholder="Not Set"
                        options={[
                          { value: 'true', label: 'Yes - Ready for Interviews' },
                          { value: 'false', label: 'No - Not Ready' }
                        ]}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex space-x-4 pt-4 border-t">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setSelectedEnrollment(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
        </Modal>
    </StaffPageShell>
  );
};

export default InterviewReadinessPage;
